import { IntegrationServices } from '@sequencehq/api'
import { Modal } from '@sequencehq/core-components'
import { Provider, useAtom, useAtomValue } from 'jotai'
import { match } from 'ts-pattern'
import {
  currentStepAtom,
  integrationStepSkippedAtom,
  selectedIntegrationAtom,
  selectedIntegrationNameAtom
} from './atoms'
import { IntegrationSelection } from './steps/IntegrationSelection'
import { CustomerSelection } from './steps/CustomerSelection'

export type StartedCustomerImport = {
  service: IntegrationServices
  customerIds: string[]
  customerNames: Record<string, string>
  startedAt?: string
}

export const CustomerImportModal = (props: {
  onClose: () => void
  isOpen: boolean
  onImportStarted: (startedImport: StartedCustomerImport) => void
}) => (
  <Modal
    onClose={props.onClose}
    data-testid="importCustomers.modal"
    isOpen={props.isOpen}
  >
    <Provider>
      <CustomerImportModalContent
        onClose={props.onClose}
        onImportStarted={props.onImportStarted}
      />
    </Provider>
  </Modal>
)

const CustomerImportModalContent = (props: {
  onClose: () => void
  onImportStarted: (startedImport: StartedCustomerImport) => void
}) => {
  const [currentStep, setCurrentStep] = useAtom(currentStepAtom)
  const selectedIntegration = useAtomValue(selectedIntegrationAtom)
  const integrationStepSkipped = useAtomValue(integrationStepSkippedAtom)

  const onCustomerSelectionBack = integrationStepSkipped
    ? props.onClose
    : () => setCurrentStep('integration')

  return (
    <>
      <CustomerImportModalHeader onClose={props.onClose} />
      {match(currentStep)
        .with('integration', () => (
          <IntegrationSelection
            onContinue={() => setCurrentStep('customers')}
            onCancel={props.onClose}
          />
        ))
        .with('customers', () => (
          <CustomerSelection
            isFirstStep={integrationStepSkipped}
            onBack={onCustomerSelectionBack}
            onImportStarted={started => {
              if (selectedIntegration) {
                props.onImportStarted({
                  service: selectedIntegration,
                  ...started
                })
              }
              props.onClose()
            }}
          />
        ))
        .exhaustive()}
    </>
  )
}

const CustomerImportModalHeader = (props: { onClose: () => void }) => {
  const currentStep = useAtomValue(currentStepAtom)
  const selectedIntegrationName = useAtomValue(selectedIntegrationNameAtom)

  const title = match(currentStep)
    .with('integration', () => 'Import customers')
    .with('customers', () => `Import customers from ${selectedIntegrationName}`)
    .exhaustive()

  return (
    <Modal.Header showCloseButton onClose={props.onClose}>
      {title}
    </Modal.Header>
  )
}
